import * as React from 'react';
import type { KbsSaglayici, PlatformRol, TesisDurum } from '@hotelcms/shared';
import { Button } from '@/components/ui/button';
import { Kart, KartBaslik, KartGovde, Rozet } from '@/components/ui/card';
import { Alan, Secim } from '@/components/ui/field';
import { ApiHatasi } from '@/lib/api';
import { adminApi, platformOturum } from '@/lib/admin-api';
import { tarihFormatla } from '@/lib/utils';
import { Kurulum } from './Kurulum';
import { YeniTesis } from './YeniTesis';

interface PlatformKullanici {
  kullanici: { id: string; ad: string; eposta: string; rol: PlatformRol };
  yetkiler: string[];
  platformDomain: string;
}

interface Tesis {
  id: string;
  ad: string;
  slug: string;
  tip: string;
  durum: TesisDurum;
  plan: string;
  odaLimiti: number;
  abonelikBitis: number | null;
  eposta: string | null;
  telefon: string | null;
  kbsSaglayici: KbsSaglayici;
  kbsTesisKodu: string | null;
  /** Sifreli bilgiler panele hicbir zaman duz metin gelmez; yalnizca bu bayrak. */
  kbsBilgileriTanimli: boolean;
  createdAt: number;
  url: string;
}

type Sayfa = 'tesisler' | 'yeni' | 'kurulum';

const DURUM_ROZET: Record<TesisDurum, { renk: 'notr' | 'basari' | 'uyari' | 'hata' | 'bilgi'; metin: string }> = {
  DENEME: { renk: 'bilgi', metin: 'Deneme' },
  AKTIF: { renk: 'basari', metin: 'Aktif' },
  ASKIDA: { renk: 'uyari', metin: 'Askıda' },
  IPTAL: { renk: 'hata', metin: 'İptal' },
};

/** otelcms.com/admin — platform yönetim paneli. */
export function AdminApp() {
  const [kimlik, setKimlik] = React.useState<PlatformKullanici | null>(null);
  const [sayfa, setSayfa] = React.useState<Sayfa>('tesisler');
  const [yukleniyor, setYukleniyor] = React.useState(true);

  React.useEffect(() => {
    if (!platformOturum.token()) {
      setYukleniyor(false);
      return;
    }
    adminApi<PlatformKullanici>('/ben')
      .then(setKimlik)
      .catch(() => platformOturum.temizle())
      .finally(() => setYukleniyor(false));
  }, []);

  if (yukleniyor) return <Bekleme />;
  if (!kimlik) return <AdminGiris onGiris={setKimlik} />;

  return (
    <div className="min-h-dvh bg-slate-50">
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-2 px-4 py-2">
          <span className="mr-3 text-lg font-bold">
            otel<span className="text-marka-600">cms</span>
            <span className="ml-2 rounded bg-slate-900 px-1.5 py-0.5 text-xs font-semibold text-white">yönetim</span>
          </span>
          <Button varyant={sayfa === 'tesisler' ? 'birincil' : 'sessiz'} boyut="sm" onClick={() => setSayfa('tesisler')}>
            Tesisler
          </Button>
          <Button varyant={sayfa === 'kurulum' ? 'birincil' : 'sessiz'} boyut="sm" onClick={() => setSayfa('kurulum')}>
            Kurulum ve dokümantasyon
          </Button>
          <div className="ml-auto flex items-center gap-3">
            <span className="text-sm text-slate-600">
              {kimlik.kullanici.ad} · {kimlik.kullanici.rol}
            </span>
            <Button
              varyant="sessiz"
              boyut="sm"
              onClick={() => {
                platformOturum.temizle();
                setKimlik(null);
              }}
            >
              Çıkış
            </Button>
          </div>
        </div>
      </header>

      {sayfa === 'yeni' ? (
        <YeniTesis platformDomain={kimlik.platformDomain} onKapat={() => setSayfa('tesisler')} />
      ) : sayfa === 'kurulum' ? (
        <Kurulum platformDomain={kimlik.platformDomain} />
      ) : (
        <TesisListesi
          yetkiler={kimlik.yetkiler}
          onYeni={() => setSayfa('yeni')}
        />
      )}
    </div>
  );
}

function Bekleme() {
  return (
    <div className="grid min-h-dvh place-items-center bg-slate-50">
      <span className="size-8 animate-spin rounded-full border-4 border-slate-200 border-t-marka-600" />
    </div>
  );
}

/* ------------------------------------------------------------- tesisler */

interface Ozet {
  tesisler: Record<string, number>;
  toplamTesis: number;
  toplamOda: number;
  yeniRezervasyon30Gun: number;
  kbsHataliBildirim: number;
}

function TesisListesi({ yetkiler, onYeni }: { yetkiler: string[]; onYeni: () => void }) {
  const [tesisler, setTesisler] = React.useState<Tesis[]>([]);
  const [ozet, setOzet] = React.useState<Ozet | null>(null);
  const [yukleniyor, setYukleniyor] = React.useState(true);
  const [hata, setHata] = React.useState<string | null>(null);
  const [parolaSonuc, setParolaSonuc] = React.useState<{ eposta: string; geciciParola: string } | null>(null);
  const [kbsDuzenlenen, setKbsDuzenlenen] = React.useState<Tesis | null>(null);

  const yukle = React.useCallback(async () => {
    setYukleniyor(true);
    try {
      const [l, o] = await Promise.all([
        adminApi<{ tesisler: Tesis[] }>('/tesisler'),
        adminApi<Ozet>('/ozet'),
      ]);
      setTesisler(l.tesisler);
      setOzet(o);
      setHata(null);
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : 'Liste alınamadı.');
    } finally {
      setYukleniyor(false);
    }
  }, []);

  React.useEffect(() => {
    void yukle();
  }, [yukle]);

  async function durumDegistir(t: Tesis, durum: TesisDurum) {
    await adminApi(`/tesisler/${t.id}`, { method: 'PATCH', body: JSON.stringify({ durum }) });
    await yukle();
  }

  async function parolaSifirla(t: Tesis) {
    const sonuc = await adminApi<{ eposta: string; geciciParola: string }>(
      `/tesisler/${t.id}/sahip-parola-sifirla`,
      { method: 'POST' },
    );
    setParolaSonuc(sonuc);
  }

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-5 p-4 sm:p-6">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold">Tesisler</h1>
        <Button varyant="ikincil" boyut="sm" onClick={yukle} yukleniyor={yukleniyor}>
          Yenile
        </Button>
        {yetkiler.includes('tesis:olustur') && (
          <Button className="ml-auto" onClick={onYeni}>
            + Yeni tesis
          </Button>
        )}
      </div>

      {hata && <div className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-red-800">{hata}</div>}

      {parolaSonuc && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900">
          <p className="font-semibold">Yeni geçici parola üretildi</p>
          <p className="font-mono text-lg">{parolaSonuc.geciciParola}</p>
          <p className="text-sm">
            {parolaSonuc.eposta} — bu parola bir daha gösterilmez.{' '}
            <button className="underline" onClick={() => setParolaSonuc(null)}>
              Kapat
            </button>
          </p>
        </div>
      )}

      {ozet && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Sayac etiket="Toplam tesis" deger={ozet.toplamTesis} />
          <Sayac etiket="Aktif" deger={ozet.tesisler.AKTIF ?? 0} renk="text-emerald-700" />
          <Sayac etiket="Toplam oda" deger={ozet.toplamOda} />
          <Sayac etiket="KBS hatası" deger={ozet.kbsHataliBildirim} renk={ozet.kbsHataliBildirim > 0 ? 'text-red-700' : undefined} />
        </div>
      )}

      <Kart>
        <KartBaslik>
          <h2 className="text-lg font-semibold">Kayıtlı tesisler</h2>
        </KartBaslik>
        <KartGovde className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-slate-600">
              <tr>
                <th className="p-3">Tesis</th>
                <th className="p-3">Adres</th>
                <th className="p-3">Plan</th>
                <th className="p-3">Durum</th>
                <th className="p-3">KBS</th>
                <th className="p-3">Açılış</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {tesisler.map((t) => (
                <tr key={t.id} className="border-t border-slate-100">
                  <td className="p-3">
                    <div className="font-semibold text-slate-900">{t.ad}</div>
                    <div className="text-xs text-slate-500">{t.eposta}</div>
                  </td>
                  <td className="p-3">
                    <a className="text-marka-600 hover:underline" href={t.url} target="_blank" rel="noreferrer">
                      {t.url.replace('https://', '')}
                    </a>
                  </td>
                  <td className="p-3">
                    {t.plan}
                    <span className="ml-1 text-xs text-slate-500">({t.odaLimiti} oda)</span>
                  </td>
                  <td className="p-3">
                    <Rozet renk={DURUM_ROZET[t.durum].renk}>{DURUM_ROZET[t.durum].metin}</Rozet>
                  </td>
                  <td className="p-3 text-xs text-slate-600">
                    <div>{KBS_SAGLAYICI_METIN[t.kbsSaglayici] ?? t.kbsSaglayici}</div>
                    {t.kbsSaglayici !== 'KAPALI' && !t.kbsBilgileriTanimli && (
                      <div className="text-amber-700">bilgiler eksik</div>
                    )}
                  </td>
                  <td className="p-3 text-xs text-slate-600">{tarihFormatla(new Date(t.createdAt).toISOString())}</td>
                  <td className="p-3">
                    <div className="flex gap-1">
                      {yetkiler.includes('tesis:parola_sifirla') && (
                        <Button boyut="sm" varyant="sessiz" onClick={() => parolaSifirla(t)}>
                          Parola
                        </Button>
                      )}
                      {yetkiler.includes('tesis:guncelle') && (
                        <Button boyut="sm" varyant="sessiz" onClick={() => setKbsDuzenlenen(t)}>
                          KBS
                        </Button>
                      )}
                      {yetkiler.includes('tesis:askiya_al') &&
                        (t.durum === 'ASKIDA' ? (
                          <Button boyut="sm" varyant="ikincil" onClick={() => durumDegistir(t, 'AKTIF')}>
                            Aç
                          </Button>
                        ) : (
                          <Button boyut="sm" varyant="sessiz" onClick={() => durumDegistir(t, 'ASKIDA')}>
                            Askıya al
                          </Button>
                        ))}
                    </div>
                  </td>
                </tr>
              ))}
              {tesisler.length === 0 && !yukleniyor && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-slate-500">
                    Henüz tesis yok. "Yeni tesis" ile ilk oteli açın.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </KartGovde>
      </Kart>

      {kbsDuzenlenen && (
        <KbsAyarModal
          tesis={kbsDuzenlenen}
          onKapat={() => setKbsDuzenlenen(null)}
          onKaydedildi={async () => {
            setKbsDuzenlenen(null);
            await yukle();
          }}
        />
      )}
    </div>
  );
}

const KBS_SAGLAYICI_METIN: Record<KbsSaglayici, string> = {
  KAPALI: 'Kapalı',
  MANUEL_FORM: 'Manuel form',
  RESMI_WS: 'Resmi web servisi',
};

/**
 * Kurulmus bir tesisin KBS baglanti bilgilerini degistirir.
 *
 * Kullanici ve sifre yalnizca YAZILIR: sunucu sifreli saklar, hicbir uctan
 * duz metin donmez. Bu yuzden alanlar bos acilir; bos birakilan alan mevcut
 * degeri korur, "temizle" ile acikca silinir.
 */
function KbsAyarModal({
  tesis,
  onKapat,
  onKaydedildi,
}: {
  tesis: Tesis;
  onKapat: () => void;
  onKaydedildi: () => void | Promise<void>;
}) {
  const [saglayici, setSaglayici] = React.useState<KbsSaglayici>(tesis.kbsSaglayici);
  const [tesisKodu, setTesisKodu] = React.useState(tesis.kbsTesisKodu ?? '');
  const [kullanici, setKullanici] = React.useState('');
  const [sifre, setSifre] = React.useState('');
  const [temizle, setTemizle] = React.useState(false);
  const [hata, setHata] = React.useState<string | null>(null);
  const [kaydediliyor, setKaydediliyor] = React.useState(false);

  // Bilgiler tanimliysa ve temizlenmiyorsa bos birakilan alanlar korunur.
  const korunuyor = tesis.kbsBilgileriTanimli && !temizle;

  async function kaydet(e: React.FormEvent) {
    e.preventDefault();
    setKaydediliyor(true);
    setHata(null);

    const govde: Record<string, unknown> = {
      kbsSaglayici: saglayici,
      kbsTesisKodu: tesisKodu.trim() || null,
    };
    if (temizle) {
      govde.kbsKullanici = null;
      govde.kbsSifre = null;
    } else {
      if (kullanici.trim()) govde.kbsKullanici = kullanici.trim();
      if (sifre) govde.kbsSifre = sifre;
    }

    try {
      await adminApi(`/tesisler/${tesis.id}`, { method: 'PATCH', body: JSON.stringify(govde) });
      await onKaydedildi();
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : 'Kaydedilemedi.');
      setKaydediliyor(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-lacivert/60 p-4" onClick={onKapat}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={kaydet}
        className="flex w-full max-w-lg flex-col gap-4 rounded-[20px] bg-white p-6"
      >
        <div>
          <h2 className="text-xl font-bold">KBS bilgileri</h2>
          <p className="mt-1 text-sm text-slate-500">
            {tesis.ad} · {tesis.url.replace('https://', '')}
          </p>
        </div>

        <Secim
          etiket="KBS sağlayıcı"
          value={saglayici}
          onChange={(e) => setSaglayici(e.target.value as KbsSaglayici)}
          secenekler={[
            { deger: 'KAPALI', metin: 'Kapalı (bildirim gönderilmez)' },
            { deger: 'MANUEL_FORM', metin: 'Manuel form (CSV indir)' },
            { deger: 'RESMI_WS', metin: 'Resmi web servisi' },
          ]}
        />

        <Alan
          etiket="Tesis kodu (TssKod)"
          value={tesisKodu}
          inputMode="numeric"
          maxLength={6}
          onChange={(e) => setTesisKodu(e.target.value.replace(/\D/g, ''))}
          ipucu="JGNK'nın tesise verdiği kod — bağlı karakol komutanlığından öğrenilir"
        />

        <Alan
          etiket="Yetkili T.C. no (KullaniciTC)"
          value={kullanici}
          inputMode="numeric"
          maxLength={11}
          autoComplete="off"
          disabled={temizle}
          onChange={(e) => setKullanici(e.target.value.replace(/\D/g, ''))}
          ipucu={korunuyor ? 'Kayıtlı — değiştirmek için yazın, dokunmamak için boş bırakın' : undefined}
        />

        <Alan
          etiket="Web servis şifresi"
          type="password"
          value={sifre}
          autoComplete="new-password"
          disabled={temizle}
          onChange={(e) => setSifre(e.target.value)}
          ipucu={
            korunuyor
              ? 'Kayıtlı — görüntülenemez. Değiştirmek için yeni şifreyi yazın.'
              : 'Şifrelenerek saklanır, bir daha görüntülenemez.'
          }
        />

        {tesis.kbsBilgileriTanimli && (
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={temizle} onChange={(e) => setTemizle(e.target.checked)} />
            Kayıtlı kullanıcı ve şifreyi sil
          </label>
        )}

        {saglayici === 'RESMI_WS' && (
          <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <strong>Resmi web servisi sabit IP ister.</strong> KBS, çağrıyı yapan IP adresinin kendi
            arayüzünde kayıtlı olmasını şart koşar. Tesis bunu sağlayamıyorsa{' '}
            <strong>Manuel form</strong> seçeneğini kullanın — akış (kuyruk, tekrar deneme, süre takibi) aynıdır.
          </div>
        )}

        {hata && <p className="text-sm text-red-600">{hata}</p>}

        <div className="flex justify-end gap-2">
          <Button type="button" varyant="sessiz" onClick={onKapat}>
            Vazgeç
          </Button>
          <Button type="submit" yukleniyor={kaydediliyor}>
            Kaydet
          </Button>
        </div>
      </form>
    </div>
  );
}

function Sayac({ etiket, deger, renk }: { etiket: string; deger: number; renk?: string }) {
  return (
    <Kart>
      <KartGovde>
        <p className="text-sm text-slate-500">{etiket}</p>
        <p className={`text-3xl font-bold ${renk ?? 'text-slate-900'}`}>{deger}</p>
      </KartGovde>
    </Kart>
  );
}

/* ---------------------------------------------------------------- giris */

function AdminGiris({ onGiris }: { onGiris: (k: PlatformKullanici) => void }) {
  const [eposta, setEposta] = React.useState('');
  const [sifre, setSifre] = React.useState('');
  const [hata, setHata] = React.useState<string | null>(null);
  const [yukleniyor, setYukleniyor] = React.useState(false);

  async function gonder(e: React.FormEvent) {
    e.preventDefault();
    setYukleniyor(true);
    setHata(null);
    try {
      const cevap = await adminApi<{ access: string; refresh: string }>('/giris', {
        method: 'POST',
        body: JSON.stringify({ eposta, sifre }),
      });
      platformOturum.yaz(cevap.access, cevap.refresh);
      onGiris(await adminApi<PlatformKullanici>('/ben'));
    } catch (err) {
      setHata(err instanceof ApiHatasi ? err.message : 'Giriş yapılamadı.');
    } finally {
      setYukleniyor(false);
    }
  }

  return (
    <div className="grid min-h-dvh place-items-center bg-slate-900 p-4">
      <Kart className="w-full max-w-sm">
        <KartGovde className="flex flex-col gap-4">
          <div>
            <h1 className="text-2xl font-bold">
              otel<span className="text-marka-600">cms</span>
            </h1>
            <p className="text-sm text-slate-500">Platform yönetim paneli</p>
          </div>
          <form onSubmit={gonder} className="flex flex-col gap-4">
            <Alan etiket="E-posta" type="email" value={eposta} onChange={(e) => setEposta(e.target.value)} required autoFocus />
            <Alan etiket="Parola" type="password" value={sifre} onChange={(e) => setSifre(e.target.value)} required />
            {hata && <p className="text-sm text-red-600">{hata}</p>}
            <Button type="submit" boyut="lg" yukleniyor={yukleniyor}>
              Giriş yap
            </Button>
          </form>
        </KartGovde>
      </Kart>
    </div>
  );
}
